import { gql, type TypedDocumentNode } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import type { CSSProperties } from 'styled-components';

import { Horizontal, SBHorizontal } from '@sorare/core/src/atoms/layout/flex';
import { BrandXS, LabelM } from '@sorare/core/src/atoms/typography';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import type { HexColor } from '@sorare/core/src/style/types';

import type { ThresholdRewardRow_threshold } from './__generated__/index.graphql.ts';

const ThresholdRow = styled(SBHorizontal)`
  align-items: center;
  width: 100%;
  &.previousThreshold,
  &.nextThreshold {
    opacity: 0.8;
  }
`;

const RewardWrapper = styled.div`
  &.completed {
    opacity: 0.5;
  }
`;

type Props = {
  threshold: ThresholdRewardRow_threshold;
  index: number;
  currentThresholdIndex: number;
  competitionColor: HexColor;
  isCompleteCollectionsCountTask: boolean;
};

export const ThresholdRewardRow = ({
  threshold,
  index,
  currentThresholdIndex,
  competitionColor,
  isCompleteCollectionsCountTask,
}: Props) => {
  const thresholdCompleted = index < currentThresholdIndex;
  const [rewardConfig] = threshold.rewardConfigs ?? [];
  let textColor: HexColor = 'var(--c-white)';
  if (thresholdCompleted) {
    textColor = 'var(--c-nd-400)';
  }

  return (
    <ThresholdRow
      className={classNames({
        nextThreshold: index > currentThresholdIndex,
        previousThreshold: index < currentThresholdIndex,
      })}
      style={{ color: textColor } as CSSProperties}
    >
      <Horizontal gap={0.5} center>
        <LabelM>
          <FormattedMessage
            defaultMessage="Lvl {level} Reward"
            values={{ level: index + 1 }}
          />
        </LabelM>
        <BrandXS color={competitionColor}>
          {isCompleteCollectionsCountTask ? (
            <FormattedMessage
              defaultMessage="{count, plural, one {1 collection} other {{count} collections}}"
              values={{ count: threshold.count }}
            />
          ) : (
            <FormattedMessage
              defaultMessage="{count, plural, one {1 card} other {{count} cards}}"
              values={{ count: threshold.count }}
            />
          )}
        </BrandXS>
      </Horizontal>
      <RewardWrapper className={classNames({ completed: thresholdCompleted })}>
        {rewardConfig ? (
          <AnyRewardConfig
            anyRewardConfig={rewardConfig}
            variant={Variant.TEXT}
          />
        ) : (
          <LabelM color="var(--c-nd-500)">–</LabelM>
        )}
      </RewardWrapper>
    </ThresholdRow>
  );
};

ThresholdRewardRow.fragments = {
  threshold: gql`
    fragment ThresholdRewardRow_threshold on CollectionTaskThreshold {
      count
      rewardConfigs {
        id
        ...AnyRewardConfig_anyRewardConfigInterface
      }
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<ThresholdRewardRow_threshold>,
};
